import { useState, useEffect } from 'react';
import { generateEventSchema, generateFAQSchema, generateOrganizationSchema, generateWebPageSchema } from '../../utils/seo';
import RegistrationModal from './components/RegistrationModal';

const RivieraCharityPage = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  useEffect(() => {
    // Add Event Schema
    const eventScript = document.createElement('script');
    eventScript.type = 'application/ld+json';
    eventScript.text = JSON.stringify(generateEventSchema());
    document.head.appendChild(eventScript);

    // Add FAQ Schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify(generateFAQSchema());
    document.head.appendChild(faqScript);

    // Add Organization Schema
    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify(generateOrganizationSchema());
    document.head.appendChild(orgScript);

    // Add WebPage Schema
    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify(generateWebPageSchema());
    document.head.appendChild(webPageScript);

    // Check for registration success/cancel in URL
    const urlParams = new URLSearchParams(window.location.search);
    const registrationStatus = urlParams.get('registration');
    
    if (registrationStatus === 'success') {
      alert('Registration successful! Thank you for joining Riviera Rounds for Charity. You will receive a confirmation email shortly.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (registrationStatus === 'cancelled') {
      alert('Registration was cancelled. Feel free to try again when you\'re ready.');
      window.history.replaceState({}, '', window.location.pathname);
    }

    return () => {
      document.head.removeChild(eventScript);
      document.head.removeChild(faqScript);
      document.head.removeChild(orgScript);
      document.head.removeChild(webPageScript);
    };
  }, []);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4cg6t6820p5j87gadq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        alert('Application submitted successfully! We will contact you soon.');
        form.reset();
      } else {
        alert('There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your application. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <RegistrationModal 
        isOpen={isRegistrationModalOpen} 
        onClose={() => setIsRegistrationModalOpen(false)} 
      />

      {/* Hero Section */}
      <header 
        className="relative h-screen bg-cover bg-center bg-no-repeat flex flex-col"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/a62459178d61176fc44eab1dbac59ab3.jpeg')`
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        
        {/* Header Navigation */}
        <div className="relative z-10 w-full px-4 sm:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            {/* Logo and Tagline */}
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                alt="Bucket List Series Logo" 
                title="Bucket List Series - Premium Golf Events" 
                className="h-16 sm:h-20 md:h-24 lg:h-28" 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/3583e5e2b78d0b331199ba2568c9c533.png"
              />
              <span className="text-white text-sm sm:text-base md:text-lg font-light italic hidden sm:inline">
                Where the game takes us
              </span>
            </div>
            
            {/* Events Button */}
            <a 
              href="https://bucketlistgolf.org/events"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-green-700 hover:bg-green-800 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-sm sm:text-base md:text-lg font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
            >
              See Our Other Events
            </a>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center relative z-10">
          <div className="text-center text-white max-w-5xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 leading-tight tracking-wide">
              Bucket List Golf Presents:
            </h1>
            <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 text-yellow-400 leading-tight">
              Riviera Rounds for Charity
            </div>
            <div className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 sm:mb-10 font-light leading-relaxed space-y-3 sm:space-y-4 max-w-3xl mx-auto">
              <p>A 'member for a day' experience on</p>
              <p>the legendary Riviera Country Club.</p>
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-yellow-400">Yes - that Riviera!</p>
              <p className="pt-2">Just days after the Genesis Open 2026 -<br />test your skills in PGA Tour conditions.</p>
              <p className="font-medium">
                <span className="text-green-400">Available: April 10th</span>
                <span className="text-gray-300 line-through ml-2 text-sm opacity-70">April 15th, 16th, 21st, 22nd, 23rd — Sold Out</span>
              </p>
              <p>Pacific Palisades, California.</p>
              <p className="text-lg sm:text-xl md:text-2xl font-medium pt-2">Will you join us?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <button 
                onClick={() => setIsRegistrationModalOpen(true)}
                className="inline-block bg-blue-800 hover:bg-blue-900 text-white px-8 sm:px-10 md:px-14 py-4 sm:py-5 text-lg sm:text-xl md:text-2xl font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                aria-label="Register for Riviera Charity Round"
              >
                Register for Riviera
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Quote Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <blockquote className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-relaxed italic">
            "Some courses you play. Some you'll never forget. Riviera isn't just a bucket list destination—it's a chance to make a difference."
          </blockquote>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8">
              Where legends play, and lives are changed.
            </h2>
          </div>
          
          {/* First Content Block with Image */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
            <div>
              <div className="text-base sm:text-lg text-gray-700 leading-relaxed space-y-4">
                <p>
                  There are rounds you play.<br/>
                  There are rounds you remember.<br/>
                  And then… there's <strong>Riviera</strong>.
                </p>
                <p>
                  Tucked into the canyons of the Pacific Palisades, Riviera Country Club has a gravity that can't be measured in yardage. It's the place where Ben Hogan built a legend, where the best in the world compete in the Genesis every February, and the <strong>Bucket List Series</strong> is opening the door to a very special few.
                </p>
              </div>
            </div>
            <div className="lg:pl-8">
              <img 
                alt="Riviera Country Club championship golf course with pristine fairways and strategic bunkers" 
                title="Riviera Country Club - Championship Golf Course Pacific Palisades CA" 
                className="w-full h-64 sm:h-80 md:h-96 object-cover object-top rounded-lg shadow-lg" 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/c9f4348468eed850a80ca038f9a68234.png"
              />
            </div>
          </div>

          {/* Second Content Block with Image */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
            <div className="lg:pr-8 order-2 lg:order-1">
              <img 
                alt="Riviera Country Club iconic clubhouse with Spanish architecture and tournament atmosphere" 
                title="Riviera Country Club Clubhouse - Pacific Palisades California" 
                className="w-full h-auto object-contain rounded-lg shadow-lg" 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/ce5bec214fd1132c62b894f22909ff3f.png"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-base sm:text-lg text-gray-700 leading-relaxed space-y-4">
                <p className="font-semibold text-lg sm:text-xl">
                  This is a walk through history disguised as a charitable round.
                </p>
                <p>
                  Three guests per outing. No fanfare, no crowds — just access to one of golf's most sacred grounds, commandeered for something that matters.
                </p>
                <p>
                  Because this isn't just about tee times. It's about impact.<br/>
                  Proceeds go to benefit <strong>"The Center for Childhood"</strong>, which helps single mothers build the skills, confidence, and community to raise their children safely and successfully.
                </p>
              </div>
            </div>
          </div>

          {/* Third Content Block with Image */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="text-base sm:text-lg text-gray-700 leading-relaxed space-y-4">
                <p>
                  This is Riviera as few will ever experience it:<br/>
                  A member hosted round alongside one of the BLS's own, caddies at your side, a lunch-and-open-bar celebration afterward, and the kind of story you'll tell forever.
                </p>
                <p>
                  Spots are rare, the cause is real, and the invitation is one you'll only see here.
                </p>
                <p className="font-semibold text-lg sm:text-xl">
                  Play Riviera. Change a life.
                </p>
              </div>
            </div>
            <div className="lg:pl-8">
              <img 
                alt="Riviera Country Club Golf Course championship layout with dramatic elevation and canyon views" 
                title="Riviera Country Club - Championship Golf Course Pacific Palisades CA" 
                className="w-full h-auto object-contain rounded-lg shadow-lg" 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/15399fe66255aacb0f05f69de5af91de.jpeg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Event Details Section */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="lg:pr-8 order-2 lg:order-1">
              <img 
                alt="Riviera Country Club Golf Course championship layout" 
                title="Riviera Country Club - Championship Golf Course Pacific Palisades CA" 
                className="w-full h-64 sm:h-80 md:h-96 object-cover object-top rounded-lg shadow-lg" 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/53a40b8177457268f64caec153e48070.jpeg"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8">Event Details</h2>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg">
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <i className="ri-calendar-line text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <strong>Date Available:</strong> April 10th, 2026
                    <div className="text-sm text-gray-500 mt-1 line-through">April 15th, 16th, 21st, 22nd, 23rd — Sold Out</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl sm:text-2xl"></i>
                  </div>
                  <div><strong>Location:</strong> Riviera Country Club<br/>Pacific Palisades, CA</div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <i className="ri-heart-line text-xl sm:text-2xl"></i>
                  </div>
                  <div><strong>Charity:</strong> Proceeds benefit The Center for Childhood</div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <i className="ri-price-tag-line text-xl sm:text-2xl"></i>
                  </div>
                  <div><strong>Price:</strong> $2,450 (per person)</div>
                </div>
              </div>
              <div className="mt-6 sm:mt-8">
                <button 
                  onClick={() => setIsRegistrationModalOpen(true)}
                  className="inline-block bg-green-700 hover:bg-green-800 text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-base sm:text-lg md:text-xl font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  aria-label="Register for Riviera Charity Round"
                >
                  Register for Riviera
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes This Event Special */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">What Makes This Event Special</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center p-6 sm:p-8 bg-white rounded-lg shadow-sm">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-blue-800 rounded-full">
                <i className="ri-trophy-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Genesis Invitational Legacy</h3>
              <p className="text-gray-700 leading-relaxed">
                Play the same fairways as Genesis Invitational legends on one of golf's most storied championship courses.
              </p>
            </div>
            <div className="text-center p-6 sm:p-8 bg-white rounded-lg shadow-sm">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-green-700 rounded-full">
                <i className="ri-vip-crown-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Exclusive Access</h3>
              <p className="text-gray-700 leading-relaxed">
                Exclusive BLS-only access and amenities with member-for-a-day privileges at this legendary venue.
              </p>
            </div>
            <div className="text-center p-6 sm:p-8 bg-white rounded-lg shadow-sm">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-red-600 rounded-full">
                <i className="ri-heart-fill text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Meaningful Impact</h3>
              <p className="text-gray-700 leading-relaxed">
                Every round supports childhood wellness through The Center for Childhood, making your game matter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Highlights Timeline */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Experience Highlights</h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600">Your Championship Day Timeline</p>
          </div>
          <div className="space-y-6 sm:space-y-8">
            <div className="flex items-start">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-800 text-white rounded-full font-bold text-lg sm:text-xl mr-4 sm:mr-6 flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Arrival & Practice Facilities</h3>
                <p className="text-base sm:text-lg text-gray-700">Access Riviera's world-class practice facilities including driving range and short game area used by PGA Tour professionals.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-green-700 text-white rounded-full font-bold text-lg sm:text-xl mr-4 sm:mr-6 flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Championship Golf with Professional Caddie</h3>
                <p className="text-base sm:text-lg text-gray-700">Play 18 holes on the legendary Genesis Invitational course with professional caddie service (walking or riding option available).</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-yellow-600 text-white rounded-full font-bold text-lg sm:text-xl mr-4 sm:mr-6 flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Gourmet Clubhouse Lunch</h3>
                <p className="text-base sm:text-lg text-gray-700">Enjoy a premium gourmet lunch in Riviera's elegant clubhouse after your round.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-red-600 text-white rounded-full font-bold text-lg sm:text-xl mr-4 sm:mr-6 flex-shrink-0">
                4
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Open Bar & Celebration</h3>
                <p className="text-base sm:text-lg text-gray-700">Celebrate your achievement with open bar service in the clubhouse (club policy applies).</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Join the Bucket List Society</h2>
            <p className="text-lg sm:text-xl text-gray-600">Signup to stay up to date on all things Bucket List Golf</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
            <form onSubmit={handleFormSubmit} data-readdy-form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="handicap" className="block text-sm font-medium text-gray-700 mb-2">
                  Golf Handicap
                </label>
                <input
                  type="text"
                  id="handicap"
                  name="handicap"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Comments (Max 500 characters)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us why you'd like to participate in this charity event..."
                ></textarea>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="bg-blue-800 hover:bg-blue-900 text-white px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h4 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 text-center mb-12 sm:mb-16">
            <a href="#faq" id="faq">Frequently Asked Questions</a>
          </h4>
          <div className="space-y-4">
            {[
              {
                question: "How does registration work?",
                answer: "All dates except April 10th, 2026 are now sold out. April 10th is the only remaining date available. Registration is on a first-come, first-served basis — payment is taken at time of registration. Once you register, you will receive a confirmation email. If April 10th fills up, additional registrants will be placed on a waitlist and notified if a spot becomes available due to a cancellation."
              },
              {
                question: "What is the cancellation policy?",
                answer: "Cancellations are allowed up to one week before the event. Since this is a last minute signup, cancellation may not be possible. Refunds are processed once the spot is filled, minus a 5% processing fee. Please email refund requests to chris.leary@bucketlistgolf.org."
              },
              {
                question: "Who can sign up for the events?",
                answer: "Events are first open to Bucket List Series members on Thousand Greens. If spots remain, registration opens to the public (including Broken Tee Society and other golf groups). Members are welcomed to sign up multiple players, but members must be registered on the Bucket List Series website on Golf Genius. Every effort will be made to pair groups together when registering multiple players."
              },
              {
                question: "What's included in the event fees?",
                answer: "Each event is custom-curated for the venue and the event. Event fees usually include greens fees, cart/caddie, meals, tee gifts, pro shop credit, breakfast, lunch, or dinner receptions, etc. Some events may include travel and lodging. Full details are listed in the 'What's Included' section on the Bucket List Series Golf Genius website."
              },
              {
                question: "What happens if the event is canceled due to weather?",
                answer: "Weather cancellations are determined at the sole discretion of the golf course. Refunds will be provided whenever applicable. If the event proceeds in extremely bad weather, impacted members will receive first right of refusal for the next year's event at that location."
              },
              {
                question: "Are singles welcomed?",
                answer: "Absolutely! Come as a group, or come as a single and meet other new friends from BLS."
              },
              {
                question: "Are there handicap or skill requirements?",
                answer: "Players of all skill levels and handicaps are welcome to register. Pace of play is very important, so we just ask you to keep up with the group in front of you. Double-bogey max scoring will be in place to ensure pace of play. A GHIN or other accredited handicap is required to compete in the NET division."
              },
              {
                question: "What is the dress code?",
                answer: "Proper golf attire is required. Collared shirts, golf pants or shorts, and golf shoes are mandatory. No denim or athletic wear."
              },
              {
                question: "What is not included in the registration fees?",
                answer: "Airfare and lodging are not included in these fees. All costs for golf, meals, caddie, and open bar (within course policy) are covered with registration."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  className="w-full px-4 sm:px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={expandedFaq === index}
                >
                  <span className="text-base sm:text-lg font-semibold text-gray-800 pr-4">{faq.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${expandedFaq === index ? 'subtract' : 'add'}-line text-lg sm:text-xl text-gray-600`}></i>
                  </div>
                </button>
                {expandedFaq === index && (
                  <div className="px-4 sm:px-6 pb-4">
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8">Ready to Make a Difference?</h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8">
            This event is more than a round—it's the journey, the impact, and the story you'll tell forever.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mb-8 sm:mb-12">
            <button 
              onClick={() => setIsRegistrationModalOpen(true)}
              className="inline-block bg-blue-800 hover:bg-blue-900 text-white px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
              aria-label="Register for Riviera Charity Round"
            >
              Register for Riviera
            </button>
            <a 
              href="https://bucketlistgolf.org/events"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-green-700 hover:bg-green-800 text-white px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              See Our Other Events
            </a>
          </div>
          <div className="border-t border-gray-200 pt-8 sm:pt-12">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8 text-gray-600">
              <div className="flex items-center">
                <div className="w-6 h-6 flex items-center justify-center mr-3">
                  <i className="ri-mail-line text-lg sm:text-xl"></i>
                </div>
                <span className="text-sm sm:text-base">Info@BucketListGolf.org</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 flex items-center justify-center mr-3">
                  <i className="ri-phone-line text-lg sm:text-xl"></i>
                </div>
                <span className="text-sm sm:text-base">(413) 222-7701</span>
              </div>
            </div>
            <div className="mt-6 sm:mt-8">
              <img 
                alt="The Bucket List Series Logo" 
                title="The Bucket List Series - Premium Golf Events" 
                className="h-16 sm:h-20 md:h-24 lg:h-28 xl:h-32 mx-auto" 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/3583e5e2b78d0b331199ba2568c9c533.png"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm sm:text-base text-gray-400">
            © 2025 The Bucket List Series. All rights reserved. |
            <a 
              href="https://readdy.ai/?origin=logo" 
              className="ml-2 hover:text-white transition-colors cursor-pointer" 
              rel="nofollow"
            >
              Website Builder
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RivieraCharityPage;
